import React, { PropsWithChildren, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, Grid } from "@mui/material";
import { Feature, SidebarFilters, SidebarFiltersVariables, UserRole } from "types/gql-types";
import { Viewer } from "components/auth/types";
import { initializeUser } from "services/analytics";
import { Loader } from "components/graphql-views";
import Context from "./context";
import InterestsSidebar from "./interests-sidebar";
import InterestsTable from "./interests-table";
import InterestsToolbar from "./interests-toolbar";
import ElasticInterestsTable from "./interests-table/elastic-table";
import { QUERY_SIDEBAR_FILTERS } from "./interests-sidebar/query";

const styles = {
  container: {
    padding: "16px 0",
  },
  sidebarContainer: {
    minHeight: 450,
  },
  tableContainer: {
    minHeight: 450,
    position: "relative" as const,
  },
  subheader: {
    boxShadow: `0 0 0 1px rgba(63,63,68,0.02),
                0 1px 3px rgba(63, 63, 68, 0.1),
                0 1px 2px rgba(0,0,0,0.02)`,
    backgroundColor: "white",
    minHeight: 58,
  },
};

type OwnProps = {
  viewer: Viewer;
};

export const isPropertyFeatureEnabled =
  (features: {name: Feature}[], featureName?: Feature):boolean =>
    !featureName || features.some(({ name }) => name === featureName);

const setupPendo = (viewer: OwnProps["viewer"], listId?: string) => {
  useEffect(() => {
    const { id: userId, email, fullName, roles, organizationId, properties, lists } = viewer;
    const role = roles.indexOf(UserRole.SUPERVISOR) === -1 ? roles[0] : "supervisor";
    const currentListName = lists?.find(({ id }) => id === listId)?.name;
    const property = properties?.find(({ name }) => name === currentListName);

    if (email && fullName && organizationId && userId && property) {
      initializeUser({
        email,
        fullName,
        organizationId,
        roles: [role],
        userId,
        propertyId: property.id,
        propertyName: property.name,
      });
    }
  }, []);
};

const LayoutContainer: React.FC<PropsWithChildren<unknown>> = ({ children }) => (
  <Grid container spacing={2} style={{ margin: 0, width: "100%", paddingTop: "16px" }} className="legacy-bs-override">
    {children}
  </Grid>
);

const Sidebar: React.FC<PropsWithChildren<unknown>> = ({ children }) => (
  <Grid size={{ xs: 12, md: 3 }} className="contact-info">
    <Card>
      <CardContent style={styles.sidebarContainer}>
        {children}
      </CardContent>
    </Card>
  </Grid>
);

const Content: React.FC<PropsWithChildren<unknown>> = ({ children }) => (
  <Grid size={{ xs: 12, md: 9 }}>
    <Card>
      <CardContent style={styles.tableContainer}>
        {children}
      </CardContent>
    </Card>
  </Grid>
);

// eslint-disable-next-line max-lines-per-function
const Interests: React.FC<PropsWithChildren<OwnProps>> = ({ viewer }) => {
  const { listId, p: page } = useParams<{ listId?: string; p?: string }>();
  const rrNavigate = useNavigate();
  if (!listId) return null;
  if (!viewer.lists?.length) return null;
  const interestListIds = listId === "all" ? viewer.lists.map(({ id }) => id) : [listId];

  // Always call setupPendo to maintain consistent hooks
  setupPendo(viewer, listId);

  const query = useQuery<SidebarFilters, SidebarFiltersVariables>(
    QUERY_SIDEBAR_FILTERS,
    {
      variables: { interestListIds },
      // Use cache-and-network to ensure we have data quickly but also get updates
      fetchPolicy: "cache-and-network",
      // Ensure we wait for the data to be available
      skip: !interestListIds.length,
    },
  );

  // Early return if we don't have data yet
  if (query.loading || !query.data?.interestListsById?.length) {
    return (
      <LayoutContainer>
        <Sidebar>
          <Loader loading={true} />
        </Sidebar>
        <Content>
          <Loader loading={true} />
        </Content>
      </LayoutContainer>
    );
  }

  const [interestList] = query.data.interestListsById;
  const features = interestList?.property?.features || [];
  const defaultSorting = interestListIds.length === 1 ? (interestList?.defaultSorting ?? "POSITION_NUMBER") : "LAST_NAME";

  const newDashEnabled = isPropertyFeatureEnabled(features, Feature.NEW_DASHBOARD);
  const weightedPrefsEnabled = isPropertyFeatureEnabled(features, Feature.WEIGHTED_PREFERENCES);
  const slideoutEnabled = isPropertyFeatureEnabled(features, Feature.INTEREST_PROFILE_SLIDEOUT);
  const sendFullAppToInterestsEnabled =
    isPropertyFeatureEnabled(features, Feature.FULL_APPLICATION);

  return (
    <Context.Provider value={query}>
      {newDashEnabled && (
        <ElasticInterestsTable viewer={viewer} interestListId={listId} pageString={page}/>)}
      <LayoutContainer>
        <Sidebar>
          <InterestsSidebar
            viewer={viewer}
            data={query.data}
            listId={listId}
            key={listId} // Force re-render when listId changes
          />
        </Sidebar>
        <Content>
          <InterestsToolbar
            defaultSorting={defaultSorting}
            viewer={viewer}
            interestListId={listId}
            weightedPrefsEnabled={weightedPrefsEnabled}
            sendFullAppToInterestsEnabled={sendFullAppToInterestsEnabled}
            slideoutEnabled={slideoutEnabled}
            page={page}
          />
          <InterestsTable
            interestListId={listId}
            viewer={viewer}
            slideoutEnabled={slideoutEnabled}
            weightedPrefsEnabled={weightedPrefsEnabled}
            sendFullAppToInterestsEnabled={sendFullAppToInterestsEnabled}
            defaultSorting={defaultSorting}
            page={page}
            navigate={(to: string) => {
              rrNavigate(to);
              return Promise.resolve();
            }}
          />
        </Content>
      </LayoutContainer>
    </Context.Provider>
  );
};

export default Interests;
